import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Fade,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Megaphone } from "lucide-react";
import { FormattedMessage } from "react-intl";
import sh from "bundles/common/utils/sh";
import AnnouncementContent from "bundles/Internal/components/common/AnnouncementContent";

const AnnouncementModal = () => {
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const [announcement, setAnnouncement] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    sh.get("/recruiter_announcements/latest", { params: { locale: i18nLocale } })
      .then((res) => {
        if (res.data?.id) {
          setAnnouncement(res.data);
          setOpen(true);
        }
      })
      .catch(() => {});
  }, []);

  const handleDismiss = useCallback(() => {
    if (!announcement?.id) return;
    setOpen(false);
    sh.post(`/recruiter_announcements/${announcement.id}/dismiss`, {
      authenticity_token: authenticityToken,
    }).catch(() => {});
  }, [announcement, authenticityToken]);

  if (!announcement) return null;

  return (
    <Dialog
      open={open}
      onClose={handleDismiss}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Fade}
      transitionDuration={250}
      PaperProps={{
        sx: {
          borderRadius: "1rem",
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background:
            "linear-gradient(135deg, hsl(178 93% 10%) 0%, hsl(153 68% 25%) 100%)",
          px: 3,
          py: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Megaphone size={26} color="#D9F752" strokeWidth={2.25} />
          <Typography
            sx={{
              color: "#fff",
              fontFamily: "Segoe UI Bold, sans-serif, system-ui",
              fontSize: 18,
            }}
          >
            <FormattedMessage id="recruiter.announcement.title" />
          </Typography>
        </Box>
        <IconButton onClick={handleDismiss} sx={{ color: "rgba(255,255,255,0.7)" }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content */}
      <DialogContent
        sx={{
          px: 3,
          py: 3,
          maxHeight: "50vh",
          overflowY: "auto",
        }}
      >
        <Typography
          sx={{
            fontFamily: "Segoe UI Bold, sans-serif, system-ui",
            fontSize: 22,
            color: "hsl(178 93% 10%)",
            mb: 2,
          }}
        >
          {announcement.title}
        </Typography>

        <AnnouncementContent text={announcement.description} />
      </DialogContent>

      {/* Footer */}
      <DialogActions sx={{ px: 3, pb: 3, pt: 0 }}>
        <Button
          onClick={handleDismiss}
          fullWidth
          disableRipple
          sx={{
            textTransform: "none",
            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
            fontSize: 15,
            py: 1.25,
            borderRadius: "0.75rem",
            backgroundColor: "hsl(153 68% 25%)",
            color: "#fff",
            "&:hover": {
              backgroundColor: "hsl(153 68% 20%)",
            },
          }}
        >
          <FormattedMessage id="recruiter.announcement.cta" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AnnouncementModal;
